import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { toastErr } from "../../api";
import { toast } from "sonner";

export const fileToDataUrl = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

export function MarkAcceptedPanel({ q, onChanged }) {
  const [open, setOpen] = useState(false);
  const [option, setOption] = useState(q.options?.[0]?.label || "");
  const [signer, setSigner] = useState(q.contact_name || "");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [po, setPo] = useState("");
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState(false);
  const seasonal = q.quote_type !== "one_time";

  const submit = async () => {
    if (!signer.trim()) return toast.error("Enter the signer's name");
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${q.id}/accept`, {
        option_label: seasonal ? option : null, signer_name: signer, accepted_date: date,
        po_number: po, signed_scan: scan,
      });
      toast.success("Marked accepted");
      setOpen(false);
      onChanged?.(r.data);
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button data-testid="quote-mark-accepted-btn" onClick={() => setOpen(true)}
        className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Customer accepted…</button>
    );
  }
  return (
    <div className="card-flat space-y-2 p-4" data-testid="mark-accepted-panel">
      <p className="text-sm font-bold">Mark accepted</p>
      {seasonal && (
        <label className="block text-xs font-bold">Accepted option
          <select data-testid="ma-option" className="field-input mt-1 w-full" value={option} onChange={(e) => setOption(e.target.value)}>
            {(q.options || []).map((o) => <option key={o.label} value={o.label}>Option {o.label}</option>)}
          </select>
        </label>
      )}
      <label className="block text-xs font-bold">Signer name
        <input data-testid="ma-signer" className="field-input mt-1 w-full" value={signer} onChange={(e) => setSigner(e.target.value)} /></label>
      <label className="block text-xs font-bold">Date
        <input data-testid="ma-date" type="date" className="field-input mt-1 w-full" value={date} onChange={(e) => setDate(e.target.value)} /></label>
      <label className="block text-xs font-bold">PO number (optional)
        <input data-testid="ma-po" className="field-input mt-1 w-full" value={po} onChange={(e) => setPo(e.target.value)} /></label>
      <label className="block text-xs font-bold">Signed scan (optional)
        <input data-testid="ma-scan" type="file" accept="image/*" className="mt-1 text-xs"
          onChange={async (e) => setScan(e.target.files?.[0] ? await fileToDataUrl(e.target.files[0]) : "")} />
        {scan && <span className="ml-2 text-primary">attached ✓</span>}
      </label>
      <button data-testid="ma-confirm" onClick={submit} disabled={busy}
        className="h-11 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
        {busy ? "Saving…" : "Mark accepted"}
      </button>
    </div>
  );
}

export function ConvertPanel({ q, onChanged }) {
  const [invite, setInvite] = useState(q.acceptance_method !== "paper"); // §12.6: paper defaults OFF
  const [busy, setBusy] = useState(false);
  const convert = async () => {
    setBusy(true);
    try {
      await api.post(`/quotes/${q.id}/convert`, { invite });
      toast.success("Converted — agreement created (inactive)");
      onChanged?.();
    } catch (e) { toastErr(e); }
    setBusy(false);
  };
  return (
    <div className="card-flat space-y-2 p-4" data-testid="quote-convert-block">
      <p className="text-sm font-bold">Accepted — convert to agreement</p>
      <p className="text-xs text-muted-foreground">
        Creates the customer (if still a lead), the property, an inactive agreement, and the season invoice
        when billing is one-invoice-on-acceptance. You activate the agreement after the walk-through.
      </p>
      <label className="flex items-center gap-2 text-xs font-semibold">
        <input type="checkbox" data-testid="convert-invite" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
        Invite the customer to the owner portal
      </label>
      <button data-testid="quote-convert-btn" onClick={convert} disabled={busy}
        className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">
        {busy ? "Converting…" : "Convert to agreement"}
      </button>
    </div>
  );
}

export function ReviseButton({ q }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const revise = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${q.id}/revise`);
      if (r.data.revision_warning) toast.warning(r.data.revision_warning, { duration: 6000 });
      else toast.success(`Revision ${r.data.display_number || r.data.quote_number} created as a draft`);
      nav(`/quotes/${r.data.id}`);
    } catch (e) { toastErr(e); }
    setBusy(false);
  };
  return (
    <button data-testid="quote-revise" onClick={revise} disabled={busy}
      className="h-10 rounded-md border border-border px-3 text-xs font-bold">Revise (new version)</button>
  );
}

export function WithdrawButton({ q }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const withdraw = async () => {
    if (!window.confirm("Withdraw this quote? It will be hidden from the list and its link stops working.")) return;
    setBusy(true);
    try {
      await api.post(`/quotes/${q.id}/withdraw`);
      toast.success("Quote withdrawn");
      nav("/quotes");
    } catch (e) { toastErr(e); }
    setBusy(false);
  };
  return (
    <button data-testid="quote-withdraw" onClick={withdraw} disabled={busy}
      className="h-10 rounded-md border border-border px-3 text-xs font-bold text-red-600">Withdraw</button>
  );
}

export function ConvertedInfo({ q }) {
  return (
    <div className="card-flat space-y-1 p-4" data-testid="quote-converted-info">
      <p className="text-sm font-bold">Converted</p>
      <p className="text-xs text-muted-foreground">Agreement created (inactive) — activate it from the property after the walk-through.</p>
      <div className="flex gap-3 text-xs font-bold text-primary">
        {q.converted_property_id && <Link to={`/properties/${q.converted_property_id}`} data-testid="quote-converted-property">Property →</Link>}
        {q.converted_invoice_id && <Link to={`/invoices/${q.converted_invoice_id}`} data-testid="quote-converted-invoice">Draft invoice →</Link>}
      </div>
    </div>
  );
}
